import { ScannerEngine } from './src/scanner/ScannerEngine.js';
import { FullscreenController } from './src/ui/FullscreenController.js';
import {
    clearReadingFeedback,
    READING_CONFIRMATION_MS,
    triggerReadingFeedback,
} from './src/ui/reading-feedback.js';
import { installViewportGuards } from './src/ui/viewport-guards.js';

const byId = (id) => document.getElementById(id);
const scannerRoot = byId('scannerRoot');
const video = byId('video');
const status = byId('scannerStatus');
const statusText = byId('statusText');
const resultModal = byId('resultModal');
const result = byId('resultado');
const resultMeta = byId('resultMeta');
const scanTip = byId('scanTip');
const startButton = byId('startButton');
const stopButton = byId('stopButton');
const rescanButton = byId('rescanButton');
const finishButton = byId('finishButton');
const technicalPanel = byId('technicalPanel');
const technicalToggle = byId('technicalToggle');
const technicalClose = byId('technicalClose');
const switchCameraButton = byId('switchCameraButton');
const torchButton = byId('torchButton');
const fullscreenButton = byId('fullscreenButton');
const torchLabel = byId('torchLabel');
const zoomGroup = byId('zoomGroup');
const zoomControl = byId('zoomControl');
const zoomValue = byId('zoomValue');

const engine = new ScannerEngine({ video });
const fullscreen = new FullscreenController({ element: scannerRoot });
const removeViewportGuards = installViewportGuards();
const helpTips = [
    'Aproxime o código da área de leitura',
    'Evite reflexos e sombras sobre o código',
    'Aumente o zoom se o código estiver distante',
    'Melhore a iluminação do ambiente',
];

let cameras = [];
let currentCamera = null;
let torchEnabled = false;
let torchSupported = false;
let zoomSupported = false;
let operationPending = false;
let tipDelay = null;
let tipInterval = null;
let tipIndex = 0;
let zoomTimer = null;
let zoomPromise = Promise.resolve();
let confirmationSequence = 0;
let modalResumePending = false;

function formatMs(value) {
    return Number.isFinite(value) ? `${Math.round(value)} ms` : '—';
}

function setStatus(message, state = 'running') {
    statusText.textContent = message;
    status.dataset.state = state;
}

function clearHelpTips() {
    window.clearTimeout(tipDelay);
    window.clearInterval(tipInterval);
    tipDelay = null;
    tipInterval = null;
    tipIndex = 0;
    scanTip.textContent = 'Mantenha o aparelho firme';
}

function startHelpTips() {
    clearHelpTips();
    tipDelay = window.setTimeout(() => {
        scanTip.textContent = helpTips[0];
        tipIndex = 1;
        tipInterval = window.setInterval(() => {
            scanTip.textContent = helpTips[tipIndex % helpTips.length];
            tipIndex++;
        }, 4500);
    }, 8000);
}

function setButtons() {
    const active = [
        'running',
        'result',
        'recovering',
        'switching',
        'initializing',
    ].includes(engine.status);

    startButton.hidden = !['error', 'stopped'].includes(engine.status);
    startButton.disabled = operationPending;
    stopButton.hidden = !active;
    stopButton.disabled = !active || operationPending;
    switchCameraButton.disabled = operationPending || cameras.length < 2;
    torchButton.disabled = operationPending || !torchSupported;
    zoomControl.disabled = operationPending || !zoomSupported;
    updateFullscreenControl();
}

function updateFullscreenControl() {
    const scannerActive = [
        'running',
        'recovering',
        'switching',
        'initializing',
    ].includes(engine.status);

    fullscreenButton.hidden = (
        !fullscreen.supported
        || fullscreen.active
        || !scannerActive
        || engine.status === 'result'
        || !resultModal.hidden
    );
    fullscreenButton.disabled = operationPending;
    fullscreenButton.title = fullscreen.lastError
        ? 'O navegador exige um toque para entrar em tela cheia.'
        : 'Ocultar os controles do navegador';
}

function setTechnicalPanel(open, { restoreFocus = false } = {}) {
    technicalPanel.hidden = !open;
    technicalToggle.setAttribute('aria-expanded', String(open));
    technicalToggle.setAttribute(
        'aria-label',
        open ? 'Fechar painel técnico' : 'Abrir painel técnico'
    );
    scannerRoot.classList.toggle('is-technical-open', open);

    if (open) {
        technicalClose.focus();
    } else if (restoreFocus) {
        technicalToggle.focus();
    }
}

function wait(milliseconds) {
    return new Promise((resolve) => {
        window.setTimeout(resolve, milliseconds);
    });
}

function renderCameras(nextCameras) {
    cameras = nextCameras;
    setButtons();
}

function renderCamera(info) {
    if (!info) {
        return;
    }

    currentCamera = info;
    const { settings, capabilities } = info;
    const zoom = capabilities.zoom;

    torchSupported = capabilities.torch;
    zoomSupported = zoom.supported;
    torchEnabled = settings.torch === true;
    torchLabel.textContent = torchSupported
        ? `Flash ${torchEnabled ? 'ON' : 'OFF'}`
        : 'Flash';
    torchButton.classList.toggle('is-active', torchEnabled);

    zoomGroup.classList.toggle('is-disabled', !zoomSupported);

    if (zoom.supported) {
        const value = settings.zoom ?? zoom.min;

        zoomControl.min = String(zoom.min);
        zoomControl.max = String(zoom.max);
        zoomControl.step = String(zoom.step || 0.1);
        zoomControl.value = String(value);
        zoomValue.textContent = `${Number(value).toFixed(1)}×`;
    } else {
        zoomControl.min = '1';
        zoomControl.max = '1';
        zoomControl.value = '1';
        zoomValue.textContent = '—';
    }

    byId('metricResolution').textContent =
        settings.width && settings.height
            ? `${settings.width}×${settings.height}`
            : '—';
    byId('metricZoom').textContent =
        settings.zoom !== null ? `${settings.zoom}×` : 'Indisponível';
    byId('metricTorch').textContent = capabilities.torch
        ? (torchEnabled ? 'ON' : 'OFF')
        : 'Indisponível';
    byId('metricFocus').textContent =
        settings.focusMode
        || (info.autofocusApplied ? 'continuous' : 'Indisponível');
    setButtons();
}

function renderMetrics(metrics) {
    byId('metricCameraFps').textContent = metrics.cameraFps.toFixed(1);
    byId('hudCameraFps').textContent = metrics.cameraFps.toFixed(0);
    byId('metricProcessingFps').textContent =
        metrics.processingFps.toFixed(1);
    byId('metricFirstRead').textContent =
        formatMs(metrics.firstReadTimeMs);
    byId('metricReadAverage').textContent =
        formatMs(metrics.averageReadIntervalMs);
    byId('metricReads').textContent = String(metrics.readCount);
    byId('metricRecoveries').textContent =
        String(metrics.recoveryCount);
}

function renderState({ status: nextStatus, attempt, phase }) {
    const messages = {
        initializing: phase === 'resuming-reading'
            ? 'Reiniciando leitura...'
            : phase === 'detector-fallback'
                ? 'Alternando detector...'
                : 'Inicializando...',
        running: 'Procurando código...',
        result: 'Código encontrado.',
        recovering: `Recuperando câmera${attempt ? ` · tentativa ${attempt}` : ''}...`,
        switching: 'Trocando câmera...',
        stopped: 'Scanner encerrado.',
        error: 'Erro na câmera.',
    };

    setStatus(messages[nextStatus] || nextStatus, nextStatus);

    if (nextStatus === 'running') {
        startHelpTips();
    } else {
        clearHelpTips();
    }

    setButtons();
}

function nextCamera() {
    if (cameras.length < 2) {
        return null;
    }

    const currentId = (
        currentCamera?.deviceId
        || currentCamera?.settings?.deviceId
    );
    const currentIndex = cameras.findIndex(
        (camera) => camera.deviceId === currentId
    );
    const currentFacing = currentCamera?.settings?.facingMode;
    const desiredFacing = currentFacing === 'environment'
        ? 'user'
        : 'environment';
    const opposite = cameras.find(
        (camera) => camera.facingMode === desiredFacing
    );

    return opposite || cameras[(currentIndex + 1) % cameras.length];
}

function scheduleZoom(value) {
    window.clearTimeout(zoomTimer);
    zoomTimer = window.setTimeout(() => {
        zoomPromise = zoomPromise
            .then(() => engine.setZoom(value))
            .catch((error) => {
                setStatus(error.message, 'error');
            });
    }, 60);
}

async function start() {
    if (operationPending) {
        return;
    }

    operationPending = true;
    confirmationSequence++;
    clearReadingFeedback(scannerRoot);
    setTechnicalPanel(false);
    resultModal.hidden = true;
    setStatus('Abrindo câmera...', 'initializing');
    setButtons();

    try {
        await fullscreen.enter();
        await engine.start();
    } catch (error) {
        if (engine.status !== 'recovering') {
            setStatus(error.message, 'error');
        }
    } finally {
        operationPending = false;
        setButtons();
    }
}

engine.on('statechange', renderState);
engine.on('cameras', renderCameras);
engine.on('camera', renderCamera);
engine.on('adapter', ({ name }) => {
    byId('metricDetector').textContent = name;
});
engine.on('metrics', renderMetrics);
engine.on('result', async (reading) => {
    const sequence = ++confirmationSequence;

    setTechnicalPanel(false);
    setStatus('Código encontrado', 'result');
    result.textContent = reading.text;
    resultMeta.textContent =
        `${reading.format} · ${reading.adapter} · ${new Date(reading.timestamp).toLocaleTimeString('pt-BR')}`;
    byId('metricLastCode').textContent = reading.text;
    triggerReadingFeedback({ root: scannerRoot });
    updateFullscreenControl();

    await wait(READING_CONFIRMATION_MS);

    if (sequence !== confirmationSequence) {
        return;
    }

    await fullscreen.exit();

    if (sequence !== confirmationSequence) {
        return;
    }

    resultModal.hidden = false;
    rescanButton.focus();
});
engine.on('error', (error) => {
    if (engine.status !== 'recovering') {
        setStatus(error.message, 'error');
    }
});

startButton.addEventListener('click', start);
stopButton.addEventListener('click', async () => {
    confirmationSequence++;
    clearReadingFeedback(scannerRoot);
    setTechnicalPanel(false);
    resultModal.hidden = true;
    engine.stop();
    await fullscreen.exit();
});

async function resumeAfterResultModal() {
    if (modalResumePending) {
        return;
    }

    modalResumePending = true;
    confirmationSequence++;
    clearReadingFeedback(scannerRoot);
    resultModal.hidden = true;
    rescanButton.disabled = true;
    finishButton.disabled = true;

    try {
        const enteringFullscreen = fullscreen.enter();

        await engine.resume();
        await enteringFullscreen;
    } catch (error) {
        setStatus(error.message, 'error');
    } finally {
        rescanButton.disabled = false;
        finishButton.disabled = false;
        modalResumePending = false;
    }
}

rescanButton.addEventListener('click', resumeAfterResultModal);
finishButton.addEventListener('click', resumeAfterResultModal);
technicalToggle.addEventListener('click', () => {
    setTechnicalPanel(technicalPanel.hidden);
});
technicalClose.addEventListener('click', () => {
    setTechnicalPanel(false, { restoreFocus: true });
});
fullscreenButton.addEventListener('click', async () => {
    fullscreenButton.disabled = true;
    await fullscreen.enter();
    updateFullscreenControl();
});
switchCameraButton.addEventListener('click', async () => {
    const camera = nextCamera();

    if (!camera || operationPending) {
        return;
    }

    operationPending = true;
    setButtons();

    try {
        await engine.switchCamera(camera.deviceId);
    } catch (error) {
        setStatus(error.message, 'error');
    } finally {
        operationPending = false;
        setButtons();
    }
});
torchButton.addEventListener('click', async () => {
    torchButton.disabled = true;

    try {
        await engine.setTorch(!torchEnabled);
    } catch (error) {
        setStatus(error.message, 'error');
    } finally {
        torchButton.disabled = false;
    }
});
zoomControl.addEventListener('input', () => {
    const value = Number(zoomControl.value);

    zoomValue.textContent = `${value.toFixed(1)}×`;
    byId('metricZoom').textContent = `${value.toFixed(1)}×`;
    scheduleZoom(value);
});

window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !technicalPanel.hidden) {
        setTechnicalPanel(false, { restoreFocus: true });
    }
});

fullscreen.onChange(({ active }) => {
    scannerRoot.classList.toggle('is-fullscreen', active);
    updateFullscreenControl();
});

window.addEventListener('pagehide', () => {
    clearHelpTips();
    window.clearTimeout(zoomTimer);
    confirmationSequence++;
    clearReadingFeedback(scannerRoot);
    removeViewportGuards();
    fullscreen.destroy();
    engine.destroy();
}, { once: true });

renderMetrics(engine.metrics.snapshot());
setButtons();
start();
